import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useParams } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Helmet from '../../common/Helmet'
import { WebsiteUrl, PercentageEndPoint } from '../../common/Constants'
import PercentageCalculator from './percentageCalculator';
import { AdsLeftDesktopFirst, AdsAboveCalcMobile } from "../../common/Ads";
import { isMobile } from 'react-device-detect';
const CommonFooter = lazy(() => import('../../common/Footer'));

// Base meta data for the page
const BASE_TITLE = 'حساب نسبة';
const BASE_DESCRIPTION = 'تعلم كيف تقوم ب';
const BASE_KEYWORDS = 'احسب نسبة, حاسبة, حساب النسبة المئوية';
// Arabic number to word mapping
const numberToArabic = {
    1: 'واحد',
    2: 'اثنان',
    3: 'ثلاثة',
    4: 'أربعة',
    5: 'خمسة',
    6: 'ستة',
    7: 'سبعة',
    8: 'ثمانية',
    9: 'تسعة',
    10: 'عشرة',
    11: 'أحد عشر',
    12: 'اثنا عشر',
    13: 'ثلاثة عشر',
    14: 'أربعة عشر',
    15: 'خمسة عشر',
    16: 'ستة عشر',
    17: 'سبعة عشر',
    18: 'ثمانية عشر',
    19: 'تسعة عشر',
    20: 'عشرون',
    21: 'واحد وعشرون',
    22: 'اثنان وعشرون',
    23: 'ثلاثة وعشرون',
    24: 'أربعة وعشرون',
    25: 'خمسة وعشرون',
    26: 'ستة وعشرون',
    27: 'سبعة وعشرون',
    28: 'ثمانية وعشرون',
    29: 'تسعة وعشرون',
    30: 'ثلاثون',
    31: 'واحد وثلاثون',
    32: 'اثنان وثلاثون',
    33: 'ثلاثة وثلاثون',
    34: 'أربعة وثلاثون',
    35: 'خمسة وثلاثون',
    36: 'ستة وثلاثون',
    37: 'سبعة وثلاثون',
    38: 'ثمانية وثلاثون',
    39: 'تسعة وثلاثون',
    40: 'اربعون',
    41: 'واحد وأربعون',
    42: 'اثنان وأربعون',
    43: 'ثلاثة وأربعون',
    44: 'أربعة وأربعون',
    45: 'خمسة وأربعون',
    46: 'ستة وأربعون',
    47: 'سبعة وأربعون',
    48: 'ثمانية وأربعون',
    49: 'تسعة وأربعون',
    50: 'خمسون',
    51: 'واحد وخمسون',
    52: 'اثنان وخمسون',
    53: 'ثلاثة وخمسون',
    54: 'أربعة وخمسون',
    55: 'خمسة وخمسون',
    56: 'ستة وخمسون',
    57: 'سبعة وخمسون',
    58: 'ثمانية وخمسون',
    59: 'تسعة وخمسون',
    60: 'ستون',
    61: 'واحد وستون',
    62: 'اثنان وستون',
    63: 'ثلاثة وستون',
    64: 'أربعة وستون',
    65: 'خمسة وستون',
    66: 'ستة وستون',
    67: 'سبعة وستون',
    68: 'ثمانية وستون',
    69: 'تسعة وستون',
    70: 'سبعون',
    71: 'واحد وسبعون',
    72: 'اثنان وسبعون',
    73: 'ثلاثة وسبعون',
    74: 'أربعة وسبعون',
    75: 'خمسة وسبعون',
    76: 'ستة وسبعون',
    77: 'سبعة وسبعون',
    78: 'ثمانية وسبعون',
    79: 'تسعة وسبعون',
    80: 'ثمانون',
    81: 'واحد وثمانون',
    82: 'اثنان وثمانون',
    83: 'ثلاثة وثمانون',
    84: 'أربعة وثمانون',
    85: 'خمسة وثمانون',
    86: 'ستة وثمانون',
    87: 'سبعة وثمانون',
    88: 'ثمانية وثمانون',
    89: 'تسعة وثمانون',
    90: 'تسعون',
    91: 'واحد وتسعون',
    92: 'اثنان وتسعون',
    93: 'ثلاثة وتسعون',
    94: 'أربعة وتسعون',
    95: 'خمسة وتسعون',
    96: 'ستة وتسعون',
    97: 'سبعة وتسعون',
    98: 'ثمانية وتسعون',
    99: 'تسعة وتسعون',
};

// Convert number to Arabic words
function getArabicNumber(number) {
    if (number <= 0) return 'رقم غير صحيح';
    if (number <= 20) return numberToArabic[number] || `رقم ${number} غير موجود`;

    if (number < 100) {
        const tens = Math.floor(number / 10) * 10;
        const units = number % 10;

        const tensWord = numberToArabic[tens] || '';
        const unitsWord = numberToArabic[units] || '';

        if (units === 0) {
            return tensWord;
        } else if (unitsWord) {
            return `${unitsWord} و${tensWord}`;
        } else {
            return `رقم ${number} غير موجود`;
        }
    }

    return numberToArabic[100] || 'رقم 100 غير موجود';
}


// Percentage Converter Component
function PercentageConverter() {
    const { percentage } = useParams();
    const validPercentage = Number(percentage);
    const [amount, setAmount] = useState(100);
    const [result, setResult] = useState(amount * (validPercentage / 100));
    const arabicPercentage = getArabicNumber(validPercentage);

    useEffect(() => {
        setResult(amount * (validPercentage / 100));
    }, [validPercentage, amount]);

    const TITLE = `${BASE_TITLE} ${validPercentage}% من مبلغ - حاسبة نسبة ${validPercentage} بالمئة - تعلم كم تساوي ${arabicPercentage} بالمئة`;
    const TITLE_H2 = `حاسبة نسبة ${validPercentage} % من  قيمة أو مبلغ `;

    const DESCRIPTION = `${BASE_DESCRIPTION} احسب نسبة ${validPercentage}% من مبلغ - حاسبة ${arabicPercentage} بالمئة - حساب كم تساوي ${arabicPercentage} بالمئة. تقوم هذه الحاسبة بحساب قيمة ونسبة ${validPercentage}% من مبلغ او قيمة معينة`;

    const KEYWORDS = `${BASE_KEYWORDS}, ${validPercentage}%, حساب نسبة ${validPercentage}%, حاسبة نسبة ${validPercentage}%, كيفية حساب ${validPercentage}%, نسبة ${validPercentage}% من مبلغ, كيفية حساب ${validPercentage} بالمئة, حساب ${validPercentage}% من القيمة, حاسبة حساب ${validPercentage} بالمئة, تعلم حساب ${validPercentage}%, شرح حساب النسبة ${validPercentage}%, كيف أحسب ${validPercentage}%, نسبة ${validPercentage} مئوية من قيمة, النسبة المئوية ${validPercentage}, أفضل حاسبة لنسبة ${validPercentage}%, حاسبة لحساب النسبة ${validPercentage}%, طرق حساب النسبة المئوية, حسابات نسبة ${validPercentage}%, شرح كيفية حساب ${validPercentage}% من مبلغ, حاسبة النسبة المئوية للمبلغ ${validPercentage}%, النسبة المئوية, حساب نسبة من مبلغ, حاسبة نسبة مئوية بالغة العربية`;
    const lang = "ar"
    const url = WebsiteUrl
    const OG_URL = `${WebsiteUrl}/${lang}/${PercentageEndPoint}/${validPercentage}`;
    const href = lang === 'ar' ? '/' : `/${lang}/`;

    return (
        <Container className="rtl">
            <Helmet
                TITLE={TITLE}
                WEBSITE_URL={url}
                DESCRIPTION={DESCRIPTION}
                KEYWORDS={KEYWORDS}
                OG_URL={OG_URL}
                LANG={lang}
            />
            <Row>
                <Col xs={12} md={9} lg={8}>
                    <div className="mainBackground text-white text-center" style={{ height: '55px' }}>
                        <h1 style={{ fontSize: '17px' }}>{TITLE}</h1>
                    </div>
                    <div className="border border-light">
                        <Row>
                            <div>
                                <p>لحساب نسبة {validPercentage}% من أي قيمة بسهولة، اتبع الخطوات التالية:</p>
                                <ul>
                                    <li>ابدأ بتحويل النسبة المئوية إلى كسر. يمكن تمثيل {validPercentage}% ككسر على النحو التالي: {validPercentage}/100.</li>
                                    <li>قم بضرب القيمة الأصلية في هذا الكسر. على سبيل المثال، إذا كانت لديك قيمة تساوي {amount} وتريد حساب {validPercentage}% منها، استخدم الصيغة التالية:
                                        <br />
                                        <strong>النسبة = (القيمة الأصلية × {validPercentage}/100)</strong>
                                    </li>
                                    <li>بمجرد إجراء الحساب، ستكون النتيجة هي قيمة {validPercentage}% من المبلغ الأصلي.</li>
                                </ul>
                                <p>على سبيل المثال، إذا كانت القيمة الأصلية هي 200 وتريد حساب {validPercentage}% منها:</p>
                                <ul>
                                    <li>تحويل {validPercentage}% إلى كسر: {validPercentage}/100.</li>
                                    <li>ضرب 200 × {validPercentage}/100 = {(200 * validPercentage) / 100}.</li>
                                    <li>النتيجة النهائية هي {(200 * validPercentage) / 100}، وهي تمثل {validPercentage}% من 200.</li>
                                </ul>
                                <p>باستخدام هذه الطريقة، يمكنك بسهولة حساب نسبة {validPercentage}% من أي مبلغ تريد.</p>
                                <p>إذا كنت ترغب في إجراء هذه الحسابات بسرعة وسهولة، يمكنك استخدام <strong>حاسبة النسبة المئوية</strong> أدناه. توفر لك هذه الحاسبة أداة فعالة لتحديد النسبة المئوية لأي قيمة بدقة. استخدمها الآن لتبسيط حساباتك!</p>
                            </div>
                        </Row>
                        <Row className='calcualtorStyle'>

                            <div className='p-3 mb-2 mainBackground text-white' style={{ textAlign: 'center' }}>
                                <h2 style={{ fontSize: '16px' }}>{TITLE_H2}</h2>
                            </div>
                            <hr />
                            <PercentageCalculator percentage={validPercentage} />
                            <hr />

                            <Suspense fallback={<div>جاري التحميل...</div>}>
                                <CommonFooter lang={lang} url={OG_URL} textName="اقتباس" />
                            </Suspense>
                            <br />

                            <br />
                        </Row>

                        {isMobile &&
                            <>
                                <hr />
                                <Row>
                                    <AdsAboveCalcMobile />
                                    <br />
                                </Row>
                            </>

                        }
                        <hr />
                        <Row>

                            <div className='alert alert-info'>
                                يمكنك ايضاً استخدام <a href={href}>حاسبة النسبة المئوية</a> لحساب النسبة المئوية لأي رقم وحساب نسبة الزيادة والنقصان
                            </div>
                            <hr />

                        </Row>

                    </div>
                </Col>
                <Col xs={12} md={3} lg={4}>
                    {!isMobile && <AdsLeftDesktopFirst />}
                </Col>
            </Row>



        </Container>
    );
}

export default PercentageConverter;